import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Info, Check } from "lucide-react";

const GENDERS = ["Woman", "Man", "Non-binary", "Genderqueer", "Genderfluid", "Trans woman", "Trans man", "Two-spirit", "Other"];
const ORIENTATIONS = ["Lesbian", "Gay", "Bisexual", "Pansexual", "Queer", "Asexual", "Demisexual", "Questioning", "Other"];

const TIER_MATERIALS = {
  gold:    "https://images.pexels.com/photos/7248765/pexels-photo-7248765.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  platinum:"https://images.unsplash.com/photo-1656055448548-e26753307fc7?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  diamond: "https://images.unsplash.com/photo-1592136184798-ca0d8e17643a?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
};
const TIER_DESCRIPTIONS = {
  gold: ["Browse member profiles", "Save your favorites", "Real Talk prompts visible", "Free forever"],
  platinum: ["Everything in Gold", "Encrypted in-app messages", "Extra optional prompts + voice notes", "Saved search filters", "Boosted visibility", "ID verification required ($15/month)"],
  diamond: ["Everything in Platinum", "Video chat w/ verified members", "Top placement in search", "Concierge support", "Diamond profile frame ($30/month)"],
};

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "", password: "", name: "", dob: "",
    gender_identity: "", sexual_orientation: "",
    preferred_partner_gender: [], preferred_partner_orientation: [],
    location: "", tier: "gold",
    accepted_terms: false, accepted_arbitration: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setForm((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter(x => x !== v) : [...p[k], v] }));

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(1, s - 1));

  const ageOk = () => {
    if (!form.dob) return false;
    const d = new Date(form.dob);
    const a = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
    return a >= 21;
  };

  const submit = async () => {
    if (!form.accepted_terms || !form.accepted_arbitration) {
      toast.error("Please accept both agreements to continue.");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...form, consent_version: "2026-02-01" });
      toast.success(`Welcome to Purple Partnerships, ${u.name}!`);
      if (form.tier !== "gold") nav("/upgrade");
      else nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create account.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen p-6 flex items-center justify-center" style={{ background: "var(--lavender)" }}>
      <div className="glass rounded-3xl p-8 sm:p-10 max-w-3xl w-full fade-up" data-testid="register-card">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Join Purple Partnerships</h1>
          <span className="text-white/60 text-sm" data-testid="register-step-indicator">Step {step} of 4</span>
        </div>
        <div className="h-1 bg-white/10 rounded-full mb-8 overflow-hidden"><div className="h-full bg-[#FF1493] transition-all" style={{ width: `${step * 25}%` }} /></div>

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl text-white font-semibold">Real Talk: who are you?</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white">Display name</Label><Input data-testid="reg-name" value={form.name} onChange={e => set("name", e.target.value)} className="bg-white/10 text-white border-white/20" /></div>
              <div><Label className="text-white">Date of birth (must be 21+)</Label><Input data-testid="reg-dob" type="date" value={form.dob} onChange={e => set("dob", e.target.value)} className="bg-white/10 text-white border-white/20" /></div>
              <div><Label className="text-white">Email</Label><Input data-testid="reg-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} className="bg-white/10 text-white border-white/20" /></div>
              <div><Label className="text-white">Password (8+ chars)</Label><Input data-testid="reg-password" type="password" value={form.password} onChange={e => set("password", e.target.value)} className="bg-white/10 text-white border-white/20" /></div>
              <div className="sm:col-span-2"><Label className="text-white">Geographic location (city, state)</Label><Input data-testid="reg-location" value={form.location} onChange={e => set("location", e.target.value)} className="bg-white/10 text-white border-white/20" placeholder="Brooklyn, NY" /></div>
            </div>
            {form.dob && !ageOk() && <p className="text-red-300 text-sm" data-testid="reg-age-error">You must be 21 or older to join.</p>}
            <div className="flex justify-end pt-4">
              <Button data-testid="reg-next-1" onClick={next} disabled={!form.name || !form.email || !form.password || !ageOk() || !form.location} className="bg-[#FF1493] hover:bg-[#d1107a] rounded-full px-8">Next</Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-white font-semibold">You & your people</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Your gender identity</Label>
                <Select value={form.gender_identity} onValueChange={(v) => set("gender_identity", v)}>
                  <SelectTrigger data-testid="reg-gender" className="bg-white/10 text-white border-white/20"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white">Your sexual orientation</Label>
                <Select value={form.sexual_orientation} onValueChange={(v) => set("sexual_orientation", v)}>
                  <SelectTrigger data-testid="reg-orientation" className="bg-white/10 text-white border-white/20"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{ORIENTATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Preferred partner gender (pick any)</Label>
              <div className="flex flex-wrap gap-2">{GENDERS.map(g =>
                <button key={g} type="button" data-testid={`reg-partner-gender-${g}`} onClick={() => toggle("preferred_partner_gender", g)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition ${form.preferred_partner_gender.includes(g) ? "bg-[#FF1493] border-[#FF1493] text-white" : "border-white/30 text-white/80 hover:bg-white/10"}`}>{g}</button>
              )}</div>
            </div>
            <div>
              <Label className="text-white mb-2 block">Preferred partner orientation (pick any)</Label>
              <div className="flex flex-wrap gap-2">{ORIENTATIONS.map(o =>
                <button key={o} type="button" data-testid={`reg-partner-orient-${o}`} onClick={() => toggle("preferred_partner_orientation", o)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition ${form.preferred_partner_orientation.includes(o) ? "bg-[#FF1493] border-[#FF1493] text-white" : "border-white/30 text-white/80 hover:bg-white/10"}`}>{o}</button>
              )}</div>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={back} className="text-white hover:bg-white/10 rounded-full">Back</Button>
              <Button data-testid="reg-next-2" onClick={next} disabled={!form.gender_identity || !form.sexual_orientation || !form.preferred_partner_gender.length || !form.preferred_partner_orientation.length} className="bg-[#FF1493] hover:bg-[#d1107a] rounded-full px-8">Next</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-white font-semibold">Pick your tier — secure the bag</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {(["gold","platinum","diamond"]).map(tier => (
                <button key={tier} type="button" data-testid={`reg-tier-${tier}`}
                  onClick={() => set("tier", tier)}
                  className={`relative rounded-2xl overflow-hidden p-5 text-left transition border-2 ${form.tier === tier ? "border-[#FF1493] scale-[1.02]" : "border-white/20 hover:border-white/40"}`}
                  style={{ aspectRatio: "1/1.15" }}>
                  <div className="absolute inset-0 sparkle" style={{ backgroundImage: `url(${TIER_MATERIALS[tier]})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  <div className="absolute inset-0 bg-black/55" />
                  <div className="relative h-full flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="font-display text-2xl font-bold text-white capitalize">{tier}</span>
                      {form.tier === tier && <Check className="text-[#FF1493]" />}
                    </div>
                    <p className="text-white/80 text-sm mt-1">{tier === "gold" ? "Free" : tier === "platinum" ? "$15/mo" : "$30/mo"}</p>
                    <Dialog>
                      <DialogTrigger asChild>
                        <span data-testid={`reg-tier-info-${tier}`} onClick={(e) => e.stopPropagation()} className="mt-auto inline-flex items-center gap-1 text-[#00FFFF] text-sm cursor-pointer hover:underline"><Info size={14} /> What does this include?</span>
                      </DialogTrigger>
                      <DialogContent className="glass border-white/15 text-white">
                        <DialogHeader><DialogTitle className="font-display text-2xl capitalize">{tier} Membership</DialogTitle></DialogHeader>
                        <ul className="space-y-2 mt-2">{TIER_DESCRIPTIONS[tier].map((d) => <li key={d} className="flex gap-2 text-white/90"><Check size={16} className="text-[#FF1493] mt-1 shrink-0" />{d}</li>)}</ul>
                      </DialogContent>
                    </Dialog>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={back} className="text-white hover:bg-white/10 rounded-full">Back</Button>
              <Button data-testid="reg-next-3" onClick={next} className="bg-[#FF1493] hover:bg-[#d1107a] rounded-full px-8">Next</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-white font-semibold">The legal — no skimming, fam</h2>
            <div className="glass-light rounded-xl p-5 max-h-56 overflow-y-auto text-sm space-y-2" data-testid="reg-legal-box">
              <p className="font-bold">Click-wrap consent</p>
              <p>By creating an account you affirm you are <strong>21+</strong>, identify as LGBTQIA+ / non-binary / queer-affirming, are not seeking marriage for immigration fraud, and you accept the <Link to="/legal/terms" className="underline text-purple-700">Terms of Service</Link>, <Link to="/legal/privacy" className="underline text-purple-700">Privacy Policy</Link>, <Link to="/legal/disclaimer" className="underline text-purple-700">Disclaimer</Link>, and Community Guidelines. Purple Partnerships facilitates <em>platonic</em> partnerships. Misrepresenting orientation, identity, or intent results in immediate termination and legal reporting where applicable.</p>
              <p className="font-bold pt-2">Arbitration & class-action waiver</p>
              <p>You and Purple Partnerships agree that any dispute will be resolved by <strong>binding individual arbitration</strong>, not in court. You waive the right to a jury trial and to participate in any class, collective, or representative action. Claims must be brought within one (1) year of the event.</p>
            </div>
            <label className="flex gap-3 items-start text-white cursor-pointer" data-testid="reg-accept-tos-row">
              <Checkbox checked={form.accepted_terms} onCheckedChange={(v) => set("accepted_terms", !!v)} data-testid="reg-accept-tos" className="mt-1 border-white/40" />
              <span>I have read and agree to the Terms of Service, Privacy Policy, Disclaimer, and Community Guidelines, and I confirm I am 21+ and a member of, or affirming of, the LGBTQIA+ community.</span>
            </label>
            <label className="flex gap-3 items-start text-white cursor-pointer" data-testid="reg-accept-arb-row">
              <Checkbox checked={form.accepted_arbitration} onCheckedChange={(v) => set("accepted_arbitration", !!v)} data-testid="reg-accept-arb" className="mt-1 border-white/40" />
              <span>I separately agree to <strong>binding individual arbitration</strong> and waive my right to participate in any class, collective, or representative action.</span>
            </label>
            <div className="flex justify-between pt-4">
              <Button variant="ghost" onClick={back} className="text-white hover:bg-white/10 rounded-full">Back</Button>
              <Button data-testid="reg-submit" onClick={submit} disabled={loading || !form.accepted_terms || !form.accepted_arbitration} className="bg-[#FF1493] hover:bg-[#d1107a] rounded-full px-8">{loading ? "Creating..." : "Create my account"}</Button>
            </div>
          </div>
        )}

        <p className="text-white/70 text-sm text-center mt-6">Already in? <Link to="/login" className="neon-pink-text font-bold" data-testid="reg-to-login">Log in</Link></p>
      </div>
    </div>
  );
}
